/**
 * Browser bridge: wraps @fairblock/stabletrust
 * ControlledConfidentialTransferClient behind a ConfidentialLib-compatible API
 * for the Babel UI.
 *
 * Production choices:
 * - Controlled client with autoApplyPending:false so deposit/transfer/withdraw
 *   never silently claim pending (Secure Claim stays explicit via the banner).
 * - Withdraw uses submitWithdraw (fee-aware gas path). Controlled.withdraw()
 *   skips the protocol ETH fee and reverts with "bad ETH fee".
 */
import { Buffer } from 'buffer';
import { ethers } from 'ethers';
import {
  ControlledConfidentialTransferClient,
  CONTRACT_ABI,
  ERC20_ABI,
  deriveKeys,
  getStabletrustContractAddress,
} from '@fairblock/stabletrust';
import wasmInit, {
  decrypt_ciphertext,
  decode_transfer_proof,
  generate_deterministic_keypair,
  generate_transfer_proof,
  generate_withdraw_proof,
} from '@fairblock/stabletrust/wasm';

globalThis.Buffer = Buffer;
if (typeof window !== 'undefined') window.Buffer = Buffer;

const KEY_CACHE_PREFIX = 'cusd_elgamal_v1:';
/** Posted on StableTrust backend request rows. Must match CUSD history filters. */
const APPLICATION_NAME = 'CUSD';

let client = null;
let tokenAddress = null;
let gatewayAddress = null;
let chainId = null;
let rpcUrl = null;
let apiBaseUrl = null;
let wasmReady = null;
let kindIds = null;
let boundEip1193 = null;

function serializeTxError(error) {
  if (error == null) return { message: 'Unknown error' };
  const out = {
    name: error.name,
    message: error.message,
    shortMessage: error.shortMessage,
    reason: error.reason,
    code: error.code,
    data: error.data,
  };
  if (error.info != null) out.info = error.info;
  if (error.receipt != null) {
    out.receipt = {
      hash: error.receipt.hash,
      status: error.receipt.status,
      blockNumber: error.receipt.blockNumber?.toString?.(),
    };
  }
  if (error.transaction != null) {
    out.transaction = {
      to: error.transaction.to,
      from: error.transaction.from,
      data:
        typeof error.transaction.data === 'string'
          ? error.transaction.data.slice(0, 200)
          : undefined,
    };
  }
  return out;
}

function toUiKeypair(keys) {
  if (!keys) return null;
  return {
    public_key: keys.publicKey || keys.public_key,
    private_key: keys.privateKey || keys.private_key,
  };
}

function fromUiKeypair(kp) {
  if (!kp) return null;
  return {
    publicKey: kp.publicKey || kp.public_key,
    privateKey: kp.privateKey || kp.private_key,
  };
}

function cacheKey(account) {
  return `${KEY_CACHE_PREFIX}${chainId}:${gatewayAddress}:${String(account).toLowerCase()}`;
}

function loadCachedKeypair(account) {
  try {
    const raw = sessionStorage.getItem(cacheKey(account));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storeKeypair(account, keypair) {
  sessionStorage.setItem(cacheKey(account), JSON.stringify(keypair));
}

function clearCachedKeypair(account) {
  try {
    sessionStorage.removeItem(cacheKey(account));
  } catch (_) {}
}

function clearAllCachedKeypairs() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(KEY_CACHE_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch (_) {}
}

function ensureKindIds() {
  if (kindIds) return kindIds;
  kindIds = {
    DEPOSIT: ethers.id('DEPOSIT').toLowerCase(),
    TRANSFER: ethers.id('TRANSFER').toLowerCase(),
    APPLY: ethers.id('APPLY').toLowerCase(),
    WITHDRAW: ethers.id('WITHDRAW').toLowerCase(),
    CREATE: ethers.id('CREATE').toLowerCase(),
  };
  return kindIds;
}

function normalizeKind(kindRaw) {
  if (kindRaw == null) return null;
  if (typeof kindRaw === 'string') return kindRaw.toLowerCase();
  try {
    return ethers.hexlify(kindRaw).toLowerCase();
  } catch {
    return null;
  }
}

function parseAccountCore(tuple) {
  if (!tuple) return null;
  return {
    exists: Boolean(tuple.exists ?? tuple[0]),
    finalized: Boolean(tuple.finalized ?? tuple[1]),
    pendingAction: Boolean(tuple.pendingAction ?? tuple[2]),
    txId: (tuple.txId ?? tuple[3])?.toString?.() || '0',
    elgamalPubkey: tuple.elgamalPubkey ?? tuple[4],
    pendingCreditCounter: (tuple.pendingCreditCounter ?? tuple[5])?.toString?.() || '0',
  };
}

function progressMessage(kind) {
  const k = normalizeKind(kind);
  if (!k) return null;
  if (k === 'pending') return 'Updating balance…';
  try {
    const ids = ensureKindIds();
    if (k === ids.DEPOSIT) return 'Deposit in progress…';
    if (k === ids.TRANSFER) return 'Transfer in progress…';
    if (k === ids.APPLY) return 'Secure claim in progress…';
    if (k === ids.WITHDRAW) return 'Withdraw in progress…';
    if (k === ids.CREATE) return 'Account setup in progress…';
  } catch (_) {}
  return null;
}

function formatConfidential(raw) {
  if (raw == null) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw / 100);
}

function getGatewayAddress() {
  return gatewayAddress;
}

function requireClient() {
  if (!client) throw new Error('StableTrust client not configured');
  return client;
}

function providerFrom(value) {
  if (!value) return null;
  if (typeof value.getSigner === 'function') return value;
  if (value.provider && typeof value.provider.getSigner === 'function') return value.provider;
  if (typeof value.request === 'function') {
    return new ethers.BrowserProvider(value, chainId ?? undefined);
  }
  return null;
}

/**
 * Route SDK contract reads through the wallet's EIP-1193 provider.
 * Public RPCs often block browser fetch (CORS) after the wallet has already
 * submitted the tx, which the SDK then wraps as "Failed to deposit".
 */
function bindWalletProvider(providerOrSigner) {
  const c = requireClient();
  const provider = providerFrom(providerOrSigner);
  if (!provider) return c;
  const eip1193 = provider.provider || provider;
  if (boundEip1193 === eip1193 && c.provider === provider) return c;
  boundEip1193 = eip1193;
  c.provider = provider;
  if (c.contract) c.contract = c.contract.connect(provider);
  return c;
}

async function readyClient(providerOrSigner) {
  const c = requireClient();
  if (providerOrSigner) bindWalletProvider(providerOrSigner);
  await ensureWasm();
  return c;
}

function resolveWasmUrl() {
  if (typeof document === 'undefined') {
    return 'confidential_transfer_proof_generation_bg.wasm';
  }
  const scripts = document.querySelectorAll('script[src]');
  for (let i = scripts.length - 1; i >= 0; i--) {
    const src = scripts[i].getAttribute('src') || '';
    if (src.includes('stabletrust.js') || src.includes('/dist/stabletrust')) {
      return new URL('confidential_transfer_proof_generation_bg.wasm', scripts[i].src).href;
    }
  }
  return new URL('dist/confidential_transfer_proof_generation_bg.wasm', document.baseURI).href;
}

async function ensureWasm() {
  if (!wasmReady) {
    wasmReady = (async () => {
      const url = resolveWasmUrl();
      await wasmInit({ module_or_path: url });
      const wasmApi = {
        init: () => wasmInit({ module_or_path: url }),
        decrypt_ciphertext,
        decode_transfer_proof,
        generate_deterministic_keypair,
        generate_transfer_proof,
        generate_withdraw_proof,
      };
      if (typeof window !== 'undefined') {
        window.__confidentialWasm = wasmApi;
        window.dispatchEvent(new Event('confidential-wasm-ready'));
      }
      return wasmApi;
    })();
  }
  return wasmReady;
}

async function getTokenDecimals() {
  if (!tokenAddress || !ethers.isAddress(tokenAddress)) {
    throw new Error('CUSD token address is not configured (REACT_APP_CUSD_EXTENSION)');
  }
  const c = requireClient();
  const token = new ethers.Contract(tokenAddress, ERC20_ABI, c.provider);
  return Number(await token.decimals());
}

async function parseTokenAmount(amountStr) {
  const decimals = await getTokenDecimals();
  return ethers.parseUnits(String(amountStr), decimals);
}

/** Convert raw ERC-20 units → contract scale (2 decimal places). */
async function rawToContractScale(rawAmount) {
  const decimals = await getTokenDecimals();
  const raw = BigInt(rawAmount);
  return Number((raw * 100n) / 10n ** BigInt(decimals));
}

function checksumAddress(account) {
  try {
    return ethers.getAddress(account);
  } catch {
    return String(account);
  }
}

/**
 * Reuse a keypair the user already signed for, so later SDK ops do not prompt
 * again. Latest SDK caches internally on `_keyCache` (no public seedKeys).
 */
function seedClientKeys(account, uiKeypair) {
  const keys = fromUiKeypair(uiKeypair);
  if (!keys?.publicKey || !keys?.privateKey) return;
  const c = requireClient();
  if (typeof c.seedKeys === 'function') {
    c.seedKeys(account, keys);
    return;
  }
  c._keyCache?.set(checksumAddress(account), keys);
}

async function getKeypair(signer, account, { forceSign = false } = {}) {
  const c = await readyClient(signer);
  if (!forceSign) {
    const cached = loadCachedKeypair(account);
    if (cached) {
      seedClientKeys(account, cached);
      return cached;
    }
  }
  const keys = await deriveKeys(
    signer,
    {
      chainId: c.config.chainId,
      contractAddress: c.config.contractAddress,
    },
    generate_deterministic_keypair,
  );
  const ui = toUiKeypair(keys);
  storeKeypair(account, ui);
  seedClientKeys(account, ui);
  return ui;
}

async function deriveKeypair(signer, account) {
  return getKeypair(signer, account, { forceSign: true });
}

async function readAccount(provider, account) {
  try {
    if (provider) bindWalletProvider(provider);
    const info = await requireClient().getAccountInfo(account);
    return parseAccountCore(info);
  } catch {
    return { exists: false, finalized: false, pendingAction: false };
  }
}

async function readPendingKind(provider, account) {
  try {
    if (provider) bindWalletProvider(provider);
    const info = await requireClient().getAccountInfo(account);
    return info?.pendingAction ? 'pending' : null;
  } catch {
    return null;
  }
}

async function decryptBalance(provider, account, keypair, kind) {
  const c = await readyClient(provider);
  const keys = fromUiKeypair(keypair);
  if (!keys?.privateKey) return 0;
  seedClientKeys(account, keypair);
  const bal = await c.getConfidentialBalance(account, keys.privateKey, tokenAddress);
  const raw = kind === 'pending' ? bal.pending.amount : bal.available.amount;
  return rawToContractScale(raw);
}

async function ensureAccount(signer, account, keypair) {
  const c = await readyClient(signer);
  if (keypair) seedClientKeys(account, keypair);
  await c.ensureAccount(signer);
  return readAccount(signer?.provider || signer, account);
}

async function deposit(signer, _account, amountStr) {
  const c = await readyClient(signer);
  const amount = await parseTokenAmount(amountStr);
  const receipt = await c.confidentialDeposit(signer, tokenAddress, amount);
  return { hash: receipt.hash, receipt };
}

async function transfer(signer, account, keypair, { recipient, amountStr }) {
  const c = await readyClient(signer);
  if (keypair) seedClientKeys(account, keypair);
  const amount = await parseTokenAmount(amountStr);
  const receipt = await c.confidentialTransfer(
    signer,
    recipient,
    tokenAddress,
    amount,
  );
  return { hash: receipt.hash, receipt };
}

async function withdraw(signer, account, keypair, { amountStr }) {
  const c = await readyClient(signer);
  if (keypair) seedClientKeys(account, keypair);
  const amount = await parseTokenAmount(amountStr);

  // Controlled.withdraw() inherits the non-fee base path. submitWithdraw goes
  // through _submitWithGasSafety, which attaches the protocol ETH/ERC-20 fee.
  const tx = await c.submitWithdraw(signer, tokenAddress, amount);
  const receipt = await tx.wait();
  if (!receipt || receipt.status === 0) {
    throw new Error('Withdrawal transaction failed');
  }

  const address = await signer.getAddress();
  // Mirror finalization + history row — same post-tx work as fee-aware withdraw().
  try {
    await c._waitForGlobalState?.(address, 'withdraw');
  } catch (e) {
    console.warn('[CUSD] withdraw finalization wait:', e?.message || e);
  }
  try {
    const requestId = await c._resolveRequestId?.(receipt, address, [
      'WithdrawRequested',
      'WithdrawProcessed',
    ]);
    const token = c._getTokenContract?.(tokenAddress);
    const tokenSymbol = token ? await token.symbol() : 'CUSD';
    const decimals = await getTokenDecimals();
    const withdrawAmount = (BigInt(amount) * 100n) / 10n ** BigInt(decimals);
    c._postRequest?.({
      requestId,
      userAddress: address.toLowerCase(),
      operationKind: 4,
      type: 'withdraw',
      transactionHash: receipt.hash,
      status: 'pending',
      details: {
        token: tokenSymbol,
        tokenAddress,
        amount: withdrawAmount.toString(),
        amountInWei: true,
      },
    });
  } catch (e) {
    console.warn('[CUSD] withdraw request tracking:', e?.message || e);
  }

  return { hash: receipt.hash, receipt };
}

async function applyPending(signer, _account, _keypair, options = {}) {
  const c = await readyClient(signer);
  const receipt = await c.applyPending(signer, {
    tokenAddress,
    ...options,
  });
  return { hash: receipt.hash, receipt };
}

function configure(opts = {}) {
  if (opts.rpcUrl) rpcUrl = opts.rpcUrl;
  if (opts.gateway) gatewayAddress = opts.gateway;
  if (opts.token) tokenAddress = opts.token;
  if (opts.chainId != null) chainId = Number(opts.chainId);
  if (opts.apiBaseUrl != null) apiBaseUrl = opts.apiBaseUrl;

  if (!rpcUrl || chainId == null) {
    console.warn('[CUSD] StableTrust configure incomplete — set REACT_APP_RPC_URL', {
      rpcUrl: Boolean(rpcUrl),
      chainId,
    });
    return;
  }

  // Prefer an explicit gateway; otherwise use the SDK default (incl. Arb One).
  if (!gatewayAddress) {
    gatewayAddress = getStabletrustContractAddress(chainId);
  }
  if (!gatewayAddress) {
    console.warn(
      '[CUSD] No confidential gateway for chain — set REACT_APP_CONFIDENTIAL_GATEWAY or use a supported chainId',
      { chainId },
    );
    return;
  }
  if (!ethers.isAddress(gatewayAddress)) {
    console.warn('[CUSD] Invalid confidential gateway address', gatewayAddress);
    return;
  }
  if (tokenAddress && !ethers.isAddress(tokenAddress)) {
    console.warn('[CUSD] Invalid CUSD token address', tokenAddress);
    tokenAddress = null;
  }

  client = new ControlledConfidentialTransferClient(rpcUrl, gatewayAddress, chainId, {
    applicationName: APPLICATION_NAME,
    apiBaseUrl: apiBaseUrl || undefined,
    autoApplyPending: false,
  });
  gatewayAddress = client.config.contractAddress;
  boundEip1193 = null;

  console.info('[CUSD] StableTrust client configured', {
    gateway: gatewayAddress,
    token: tokenAddress,
    chainId,
    rpc: rpcUrl,
    applicationName: APPLICATION_NAME,
    autoApplyPending: false,
  });

  ensureWasm().catch((err) => {
    console.error('[CUSD] WASM init failed:', err);
  });
}

const ConfidentialLib = {
  configure,
  bindWalletProvider,
  getGatewayAddress,
  GATEWAY_ABI: CONTRACT_ABI,
  normalizeKind,
  progressMessage,
  parseAccountCore,
  getKeypair,
  deriveKeypair,
  loadCachedKeypair,
  clearCachedKeypair,
  clearAllCachedKeypairs,
  readAccount,
  readPendingKind,
  decryptBalance,
  ensureAccount,
  deposit,
  transfer,
  withdraw,
  applyPending,
  formatConfidential,
  serializeTxError,
  getClient: () => client,
};

if (typeof window !== 'undefined') {
  window.ConfidentialLib = ConfidentialLib;
  window.StableTrustBridge = { ConfidentialLib, ensureWasm };
  window.__confidentialWasmReady = ensureWasm().catch((err) => {
    console.error('[CUSD] early WASM init failed:', err);
  });
}

export { ConfidentialLib, configure, ensureWasm };
